import { BasePlugin, Events, Errors } from 'xgplayer'
import { EVENT } from 'xgplayer-streaming-shared'
import { EXTEND_EVENTS } from './rts/constants'
import Rts from './rts'
import { isSupported, getCapacity } from './rts/isSupported'
import { RtsOption } from './rts/options'


export class RtsPlugin extends BasePlugin {
    private _rts: Rts | null = null

    private _rtsOpts: RtsOption | null = null

    private _originSwitchFn?: (url: string) => void

    get pc () {
      return this._rts?.pc
    }

    get jitterBufferDelay () {
      return this._rts?.getJitterBufferDelay() || 0
    }

    // player-logger 中会用到 loader.finnalUrl 等字段
    get loader () {
      return this._rts?.loader
    }

    get videoTrack () {
      return this._rts?.videoTack
    }

    get audioTrack () {
      return this._rts?.audioTrack
    }

    get core () {
      return this._rts
    }

    get version () {
      // @ts-expect-error will replace 
      return __VERSION__
    }

    static get pluginName () {
      return 'rts'
    }

    static isSupported () {
      return isSupported()
    }

    static getCapacity () {
      return getCapacity()
    }

    static async isSupportedH264 (targetProfileLevel?: string) {
      try {
        const res = await RtsPlugin.getCapacity()
        let hasH264 = res.video?.filter(x => x.codec === 'H264' && (!targetProfileLevel || x.config?.indexOf(targetProfileLevel) !== -1)).length

        if (!hasH264) {
          // repeat check
          const res1 = await RtsPlugin.getCapacity()
          hasH264 = res1.video?.filter(x => x.codec === 'H264' && (!targetProfileLevel || x.config?.indexOf(targetProfileLevel) !== -1)).length
        }

        return !!hasH264

      } catch (e) {
        return false
      }

    }

    beforePlayerInit () {
      this._init()

      if (!this._originSwitchFn) {
        this._originSwitchFn = this.player.switchURL?.bind(this.player)
      }

      this.player.switchURL = this._onSwitchURL

      this.player?.useHooks('replay', () => this._rts?.switchURL())

      this.on(Events.URL_CHANGE, this._onSwitchURL)
      this.on(Events.DESTROY, this.destroy)

      try {
        BasePlugin.defineGetterOrSetter(this.player, {
          __url: {
            get: () => {
              try {
                return URL.createObjectURL(new MediaSource())
              } catch (e) {
              // for no MediaSource on ios
                if (!this.player?.video?.srcObject) {
                  this.player.video.srcObject = new MediaStream()
                }
              }
            }
          }
        })
      } catch (e) {
      // NOOP
      }
    }

    getStats () {
      return this._rts?.getStats()
    }

    getStatsSnapshoot () {
      return this._rts?.getStatsSnapshoot()
    }

    _init = () => {
      this._rts?.destroy()

      const config = this.player.config

      const rtsOpts = config.rts || {}

      this._rtsOpts = rtsOpts

      this._rts = new Rts({
        media: this.player.video,
        ...rtsOpts
      })

      this._transErrorEvent()
      this._transCoreEvent(EVENT.LOAD_START)
      this._transCoreEvent(EVENT.TTFB)
      this._transCoreEvent(EVENT.LOAD_RESPONSE_HEADERS)
      this._transCoreEvent(EVENT.LOAD_RETRY)
      this._transCoreEvent(EVENT.METADATA_PARSED)
      this._transCoreEvent(EVENT.SEI)
      this._transCoreEvent(EXTEND_EVENTS.RTC_STATE_CHANGE)
      this._transCoreEvent(EXTEND_EVENTS.RTC_NETWORK)
      this._transCoreEvent(EXTEND_EVENTS.RTC_FIRST_FRAME)

      this._rts.load(config.url)
    }

    _onSwitchURL = (url: string) => {
      if (this._rts) {
        this.player.config.url = url
        this._rts.switchURL(url)
      }
    }

    _transErrorEvent () {
      this._rts?.on(EVENT.ERROR, (err) => {
        if (!this.player) return

        const e = new Errors(this.player, err)

        // auto degrade
        const { backupConstruct, backupURL } = this._rtsOpts || {}
        if (backupURL) {
          const player = this.player

          player.emit('core_event', {
            ...{
              url: player.config.url,
              code: e.errorCode,
              message: e.message,
              httpCode: e.httpCode
            },
            eventName: 'core.degrade'
          })

          player.emit('degrade', {
            url: player.config.url,
            code: e.errorCode,
            message: e.message,
            httpCode: e.httpCode
          })

          player.unRegisterPlugin('rts')
          player.switchURL = this._originSwitchFn
          this._rts = null
          player.rts = null
          if (player.video?.srcObject) {
            player.video.srcObject = null
          }
          player.config.url = backupURL
          if (backupConstruct) {
            const newPlugin = player.registerPlugin(backupConstruct)
            newPlugin.beforePlayerInit()
          } else {
            player.video.src = backupURL
          }
          return
        }

        // emit error
        this.player.emit(Events.ERROR, e)
      })

      this._rts?.on('waiting', () => {
        this.player?.emit('waiting')
      })
    }

    _transCoreEvent (eventName: string) {
      this._rts?.on(eventName, (e) => {
        if (this.player) {
          this.player.emit('core_event', {
            ...e,
            eventName
          })
        }
      })
    }

    destroy = () => {
      this.player.switchURL = this._originSwitchFn
      this._rts?.destroy()
      this._rts = null
    }
}
